import { useMemo, type ComponentProps } from 'react'
import { useInView } from 'react-intersection-observer'
import { Experiments } from 'constants/experiments'
import { MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY } from 'constants/recommendations'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferList from 'hooks/Offers/useOfferList'
import OfferRecommendations, { RECOMMENDATION_TYPES } from './OfferRecommendations'

type OfferRecommendationsProps = ComponentProps<typeof OfferRecommendations>

interface Props extends Omit<OfferRecommendationsProps, 'overrideOfferList'> {
  isSpoofed?: boolean
}

/**
 * Fetches graph and offer-to-offer recommendations in parallel, then selects
 * the assigned model only when both can provide a complete carousel.
 */
function SimilarOfferRecommendationsExperiment({ filters, isSpoofed = false, ...props }: Props) {
  const [carouselRef, carouselInView] = useInView({ triggerOnce: true })
  const graphFilters = useMemo<App.OfferListFilters | undefined>(() => {
    return filters ? { ...filters, lere: 'graph', offerTypes: RECOMMENDATION_TYPES } : undefined
  }, [filters])
  const offerToOfferFilters = useMemo<App.OfferListFilters | undefined>(() => {
    return filters ? { ...filters, lere: 'lere-o2o', offerTypes: RECOMMENDATION_TYPES } : undefined
  }, [filters])
  const offerToOfferList = useOfferList(offerToOfferFilters, { disabled: isSpoofed || !offerToOfferFilters })
  const graphOfferList = useOfferList(graphFilters, { disabled: isSpoofed || !graphFilters })
  const loadingOfferList = useMemo<App.OfferList>(() => ({
    ...graphOfferList,
    fetching: true,
    offerIds: [],
  }), [graphOfferList])

  const bothRequestsFinished = !graphOfferList.fetching && !offerToOfferList.fetching
  const bothHaveEnoughOffers = graphOfferList.offerIds.length >= MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY &&
    offerToOfferList.offerIds.length >= MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY
  const isEligibleForExperiment = !isSpoofed && bothRequestsFinished && bothHaveEnoughOffers
  const { isTurnedOn, variation } = useExperiment(Experiments.lereSimilarToCarousel, {
    canActivate: carouselInView && isEligibleForExperiment,
  })
  const useOfferToOfferRecommendation = isEligibleForExperiment && isTurnedOn && variation === 'on'
  const selectedOfferList = useOfferToOfferRecommendation ? offerToOfferList : graphOfferList
  const selectedFilters = useOfferToOfferRecommendation ? offerToOfferFilters : graphFilters

  if (!bothRequestsFinished) {
    return <div ref={carouselRef}>
      <OfferRecommendations {...props} filters={graphFilters} overrideOfferList={loadingOfferList} />
    </div>
  }

  return <div ref={carouselRef}>
    <OfferRecommendations {...props} filters={selectedFilters} overrideOfferList={selectedOfferList} />
  </div>
}

export default SimilarOfferRecommendationsExperiment
